import React, { useCallback, useEffect, useRef } from 'react';
import { organisationsAPI } from '../../../organisations/api/organisationsAPI';
import { useActivitySection } from '../../hooks/useActivitySection';
import ActivitySubnav from './activity/ActivitySubnav';
import ActivityOverview from './activity/ActivityOverview';
import WritingSection from './activity/WritingSection';
import ExamSection from './activity/ExamSection';
import QuizSection from './activity/QuizSection';
import InterviewSection from './activity/InterviewSection';
import CasesSection from './activity/CasesSection';
import ApplicationsSection from './activity/ApplicationsSection';
import { scrollToSection } from './activity/lib/scrollToSection';

// Trait trends and the concepts card cover the latest essays and the whole
// history, whatever the period, so they are keyed by this instead.
const ANY_PERIOD = 'any';
const TREND_ESSAYS = 4;

const api = organisationsAPI;
const fetchSummary = (id, period) => api.getStudentActivitySummary(id, { period });
const fetchWriting = (id, period) => api.getStudentActivityWriting(id, { period });
const fetchTrends = (id) => api.getStudentTraitTrends(id, { limit: TREND_ESSAYS });
const fetchExams = (id, period) => api.getStudentActivityExams(id, { period });
const fetchQuizzes = (id, period) => api.getStudentQuizzes(id, { period });
const fetchInterviews = (id, period) => api.getStudentActivityInterviews(id, { period });
const fetchCases = (id, period) => api.getStudentActivityCases(id, { period });
const fetchApplications = (id, period) => api.getStudentActivityApplications(id, { period });
const fetchEngagement = (id, period) => api.getStudentActivityEngagement(id, { period });
const fetchChat = (id, period) => api.getStudentActivityChat(id, { period });

const isEmptyList = (key) => (data) => !data[key].length;
const noWriting = isEmptyList('items');
const noEssays = isEmptyList('essays');
const noExams = isEmptyList('exams');
const noQuizzes = isEmptyList('quizzes');
const noRuns = isEmptyList('runs');
const noCases = (data) => !data.cases.length
  && data.scoring?.reasoning?.current == null
  && data.scoring?.knowledge?.current == null;

/**
 * The Activity tab: a sticky bar of section links and the period, then one
 * section per Activity endpoint. The summary loads first; once it has
 * answered (either way) the other sections load in parallel. Each section
 * has its own loading, empty and error state with Retry, so one failure
 * never blanks the page.
 *
 * `onCommentPosted(item, comment)` is
 * told about a comment posted on an essay (Writing) or a case answer
 * (Cases); `onOpenPaper(ref)` opens an Exam practice paper's review
 * (`paperHref(ref)` is its URL), and `onOpenQuiz(sessionId)` a Quiz
 * generator quiz's (`quizHref(sessionId)`).
 *
 * `section` is the dashboard's ?section=: the section last moved to from
 * the subnav, a flag, a tile or Applications, each of which scrolls there
 * (lib/scrollToSection) and tells `onSectionChange(key)`, which records
 * it. On load, once every section has answered (so nothing above it still
 * changes height), the page moves to it, once per value.
 */
const StudentActivityPanel = ({
  studentId, period, onPeriodChange, onCommentPosted, onOpenPaper, paperHref,
  onOpenQuiz, quizHref, section = null, onSectionChange,
}) => {
  const summary = useActivitySection(fetchSummary, studentId, period);
  const enabled = summary.status !== 'loading';
  const writing = useActivitySection(fetchWriting, studentId, period, { enabled, isEmpty: noWriting });
  const trends = useActivitySection(fetchTrends, studentId, ANY_PERIOD, { enabled, isEmpty: noEssays });
  const exams = useActivitySection(fetchExams, studentId, period, { enabled, isEmpty: noExams });
  const quizzes = useActivitySection(fetchQuizzes, studentId, period, { enabled, isEmpty: noQuizzes });
  const interviews = useActivitySection(fetchInterviews, studentId, period, { enabled, isEmpty: noRuns });
  const cases = useActivitySection(fetchCases, studentId, period, { enabled, isEmpty: noCases });
  const applications = useActivitySection(fetchApplications, studentId, period, { enabled });
  const engagement = useActivitySection(fetchEngagement, studentId, period, { enabled });
  const chat = useActivitySection(fetchChat, studentId, period, { enabled });

  const settled = [summary, writing, trends, exams, quizzes, interviews, cases, applications, engagement]
    .every((s) => s.status !== 'loading');

  // The section already moved to: one a click went to is not moved to again
  // when it reaches the URL.
  const movedTo = useRef(null);
  useEffect(() => {
    if (!section || !settled || movedTo.current === section) return;
    movedTo.current = section;
    scrollToSection(section);
  }, [section, settled]);

  const jump = useCallback((key) => {
    movedTo.current = key;
    scrollToSection(key);
    if (onSectionChange) onSectionChange(key);
  }, [onSectionChange]);

  const moreWriting = useCallback(
    (cursor) => api.getStudentActivityWriting(studentId, { period, cursor }), [studentId, period],
  );
  const moreQuizzes = useCallback(
    (cursor) => api.getStudentQuizzes(studentId, { period, cursor }), [studentId, period],
  );
  const moreInterviews = useCallback(
    (cursor) => api.getStudentActivityInterviews(studentId, { period, cursor }), [studentId, period],
  );
  const moreCases = useCallback(
    (cursor) => api.getStudentActivityCases(studentId, { period, cursor }), [studentId, period],
  );

  return (
    <div className="td-act">
      <ActivitySubnav period={period} onPeriodChange={onPeriodChange} onJump={jump} />
      <ActivityOverview state={summary} period={period} onJump={jump} engagementState={engagement} />
      <ExamSection state={exams} onOpenPaper={onOpenPaper} paperHref={paperHref} />
      <CasesSection
        state={cases}
        studentId={studentId}
        fetchPage={moreCases}
        onCommentPosted={onCommentPosted}
      />
      <ApplicationsSection
        state={applications}
        studentId={studentId}
        onJump={jump}
        chatState={chat}
      />
      <WritingSection
        state={writing}
        trendsState={trends}
        studentId={studentId}
        fetchPage={moreWriting}
        onCommentPosted={onCommentPosted}
      />
      <QuizSection state={quizzes} fetchPage={moreQuizzes} onOpenQuiz={onOpenQuiz} quizHref={quizHref} />
      <InterviewSection state={interviews} studentId={studentId} fetchPage={moreInterviews} />
    </div>
  );
};

export default StudentActivityPanel;
